/**
 * TIKTOK CAMPAIGN WORKSPACE — /tiktok/workspace
 *
 * One guided flow: Campaign → Ad groups & ads → Review & publish, on a single
 * page with a step rail on the left and a live TikTok-style phone preview on
 * the right (bound to the ad being edited).
 *
 *   • Many ad groups per campaign, many ads per ad group, held as a local
 *     draft (auto-saved to localStorage by TiktokWorkspaceProvider).
 *   • Nothing is created until Publish, which then creates everything in
 *     dependency order via the save helpers. Publishing is RESUMABLE: created
 *     entities are locked in the draft and skipped on retry.
 *   • Everything starts paused; Activate flips the tree on (activateTree).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronRight, Layers, Plus } from 'lucide-react';
import { TiktokShell } from '@/components/tiktok/TiktokShell';
import { Button } from '@/components/Button';
import { TiktokWorkspaceProvider, useTiktokWorkspace } from '@/contexts/TiktokWorkspaceContext';
import { tiktokService } from '@/services/tiktok.service';
import { adDestinationFor, defaultGoalFor } from '@/lib/tiktok-matrix';
import { CampaignFormPanel } from '@/components/tiktok/workspace/CampaignFormPanel';
import { AdGroupCard } from '@/components/tiktok/workspace/AdGroupCard';
import { AdPreviewPanel } from '@/components/tiktok/workspace/AdPreviewPanel';
import { ReviewPublishPanel } from '@/components/tiktok/workspace/ReviewPublishPanel';
import { StepRail, type StepKey } from '@/components/tiktok/workspace/StepRail';
import { ActivateDialog } from '@/components/tiktok/workspace/ActivateDialog';
import { treeIssues } from '@/components/tiktok/workspace/validation';
import {
  OBJECTIVE_LABELS, Pill, TikTokGlyph, groupForm, type Identity,
} from '@/components/tiktok/workspace/workspaceShared';

function WorkspaceInner() {
  const navigate = useNavigate();
  const {
    campaign, adGroups, activeLevel, activeAdGroupId, activeAdId, activeAd, activeAdGroup,
    setActiveLevel, addAdGroup, deleteAdGroup, duplicateAdGroup,
    saveDraft, discardDraft, reset,
  } = useTiktokWorkspace();

  // The draft remembers which level was open; the rail maps it to a step.
  const [step, setStep] = useState<StepKey>(activeLevel === 'campaign' ? 'campaign' : 'groups');
  const [openGroupId, setOpenGroupId] = useState<string | null>(activeAdGroupId ?? adGroups[0]?.id ?? null);
  const [showActivate, setShowActivate] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // addAdGroup / duplicateAdGroup / focusAd all move the context's active
  // group — open that card so the user lands on what they just created.
  useEffect(() => {
    if (activeAdGroupId) setOpenGroupId(activeAdGroupId);
  }, [activeAdGroupId]);

  // Shared lookups for every ad card.
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [identitiesLoading, setIdentitiesLoading] = useState(true);
  const reloadIdentities = useCallback(async () => {
    setIdentitiesLoading(true);
    try { setIdentities(await tiktokService.listIdentities()); } catch { /* interceptor toasts */ } finally { setIdentitiesLoading(false); }
  }, []);
  useEffect(() => { void reloadIdentities(); }, [reloadIdentities]);

  const [leadForms, setLeadForms] = useState<Array<{ pageId: string; title: string }> | null>(null);
  const [leadFormsLoading, setLeadFormsLoading] = useState(false);
  const reloadLeadForms = useCallback(async () => {
    setLeadFormsLoading(true);
    try { setLeadForms(await tiktokService.listLeadForms()); } catch { setLeadForms([]); } finally { setLeadFormsLoading(false); }
  }, []);

  const cf = campaign.formData as Record<string, string>;
  const objective = cf.objectiveType || campaign.objective || undefined;
  const issues = useMemo(() => treeIssues(campaign, adGroups, identities), [campaign, adGroups, identities]);
  const issuesFor = (k: StepKey) => (k === 'campaign' ? issues.campaign : k === 'groups' ? issues.groups : []);
  const doneFor = (k: StepKey) => (k === 'campaign'
    ? campaign.isLocked
    : k === 'groups'
      ? adGroups.length > 0 && adGroups.every((g) => g.isLocked && g.ads.length > 0 && g.ads.every((a) => a.isLocked))
      : false);

  const allAds = useMemo(() => adGroups.flatMap((g) => g.ads.map((a) => ({ g, a }))), [adGroups]);
  // Preview follows the focused ad; otherwise the first ad.
  const previewPair = (activeAd && activeAdGroup) ? { g: activeAdGroup, a: activeAd } : allAds[0] ?? null;
  const previewIdentity = identities.find((i) => i.identityId === (previewPair?.a.formData as Record<string, unknown> | undefined)?.identityId);
  const previewDestination = previewPair
    ? adDestinationFor(objective, ((groupForm(previewPair.g).optimizationGoal as string) || defaultGoalFor(objective)))
    : 'required';

  const focusAd = (groupId: string, adId: string) => {
    setActiveLevel('ad', groupId, adId);
    setOpenGroupId(groupId);
  };

  const handleAddGroup = () => {
    const id = addAdGroup();
    setOpenGroupId(id);
  };

  const handleActivate = async () => {
    if (!campaign.apiId) { toast.error('Campaign not saved'); return; }
    setIsPublishing(true);
    try {
      const adGroupIds = adGroups.filter((a) => a.apiId).map((a) => a.apiId!);
      const adIds = adGroups.flatMap((a) => a.ads.filter((x) => x.apiId).map((x) => x.apiId!));
      await tiktokService.activateTree({ tiktokCampaignId: campaign.apiId, tiktokAdGroupIds: adGroupIds, tiktokAdIds: adIds });
      const activatedApiId = campaign.apiId;
      toast.success('Campaign activated on TikTok!');
      setShowActivate(false);
      reset();
      navigate(`/tiktok/campaigns?activated=${activatedApiId}`);
    } catch {
      // axios interceptor toasts the backend error already.
    } finally {
      setIsPublishing(false);
    }
  };

  const totalAds = allAds.length;
  const lockedAdGroups = adGroups.filter((a) => a.apiId).length;
  const startsPaused = (cf.operationStatus ?? 'DISABLE') !== 'ENABLE';

  return (
    <TiktokShell
        title="Campaign workspace"
        section="Workspace"
        description="Set your goal, build your ads and review everything before publishing."
      >

      <div className="w-full">
        <div className="tt-workspace" data-step={step}>
          {/* step rail */}
          <div className="contents">
            <StepRail
              step={step}
              onStep={setStep}
              issuesFor={issuesFor}
              doneFor={doneFor}
              adGroupCount={adGroups.length}
              adCount={totalAds}
              onSaveDraft={() => {
                saveDraft();
                // The save is instant and silent (localStorage) — without this
                // toast the button looked broken (user report, 2026-08-29).
                toast.success('Draft saved — it will be restored next time you open the workspace');
              }}
              onClose={() => { saveDraft(); navigate('/tiktok/campaigns'); }}
              onDiscard={() => {
                if (window.confirm('Discard this draft and start over? Anything not yet saved to TikTok will be lost.')) {
                  discardDraft();
                  setStep('campaign');
                  toast.success('Draft discarded');
                }
              }}
            />
          </div>

          {/* main */}
          <div className="min-w-0 flex-1 space-y-4">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-2xl border border-border bg-surface px-4 py-2.5 text-xs text-text-muted shadow-soft">
              <span className="flex items-center gap-1.5 text-text-primary"><TikTokGlyph size={12} /> TikTok Ads</span>
              <span>Objective: <span className="font-medium text-text-primary">{objective ? (OBJECTIVE_LABELS[objective] ?? objective) : '—'}</span></span>
              <Pill tone={startsPaused ? 'muted' : 'ok'}>{startsPaused ? 'starts paused' : 'starts active'}</Pill>
            </div>

            {/* ── STEP 1: campaign ── */}
            {step === 'campaign' && (
              <CampaignFormPanel onNext={() => { setStep('groups'); if (adGroups.length === 0) handleAddGroup(); }} />
            )}

            {/* ── STEP 2: ad groups + ads ── */}
            {step === 'groups' && (
              <>
                {adGroups.map((g, i) => (
                  <AdGroupCard
                    key={g.id}
                    group={g}
                    index={i}
                    objective={objective}
                    open={openGroupId === g.id}
                    onToggle={() => setOpenGroupId(openGroupId === g.id ? null : g.id)}
                    identities={identities}
                    identitiesLoading={identitiesLoading}
                    reloadIdentities={reloadIdentities}
                    leadForms={leadForms}
                    leadFormsLoading={leadFormsLoading}
                    reloadLeadForms={reloadLeadForms}
                    activeAdId={activeAdId}
                    onFocusAd={(adId) => focusAd(g.id, adId)}
                    onDelete={() => {
                      if (window.confirm(`Remove "${g.name}" and its ${g.ads.length} ad${g.ads.length === 1 ? '' : 's'} from this draft?`)) deleteAdGroup(g.id);
                    }}
                    onDuplicate={() => { const id = duplicateAdGroup(g.id); setOpenGroupId(id); }}
                  />
                ))}

                {adGroups.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-border bg-surface-alt/40 px-6 py-10 text-center">
                    <Layers size={28} className="mx-auto text-text-muted" />
                    <p className="mt-2 text-sm font-medium text-text-primary">No ad groups yet</p>
                    <p className="mt-1 text-xs text-text-muted">An ad group holds the audience, budget and schedule; its ads hold the creative.</p>
                  </div>
                )}

                <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} onClick={handleAddGroup}>
                  Add another ad group
                </Button>

                <div className="flex justify-between">
                  <Button variant="secondary" onClick={() => setStep('campaign')}>Back</Button>
                  <Button onClick={() => setStep('review')} iconRight={<ChevronRight size={15} />}>Next: Review</Button>
                </div>
              </>
            )}

            {/* ── STEP 3: review + publish ── */}
            {step === 'review' && (
              <ReviewPublishPanel
                identities={identities}
                onEditCampaign={() => setStep('campaign')}
                onEditGroups={() => setStep('groups')}
                onActivate={() => setShowActivate(true)}
                onFocusAd={(gid, aid) => { focusAd(gid, aid); setStep('groups'); }}
              />
            )}
          </div>

          {/* live preview */}
          {step !== 'campaign' && <div className="tt-workspace-preview">
            <div>
              <AdPreviewPanel ad={previewPair?.a ?? null} identity={previewIdentity} destination={previewDestination} />
            </div>
          </div>}
        </div>
      </div>

      {showActivate && (
        <ActivateDialog
          adGroupCount={lockedAdGroups}
          adCount={totalAds}
          onConfirm={handleActivate}
          onCancel={() => setShowActivate(false)}
          isPublishing={isPublishing}
        />
      )}
    </TiktokShell>
  );
}

export default function TiktokWorkspace() {
  const { draftId } = useParams<{ draftId?: string }>();

  return (
    <TiktokWorkspaceProvider resumeDraftId={draftId}>
      <WorkspaceInner />
    </TiktokWorkspaceProvider>
  );
}
